import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { unstable_usePrompt, useBeforeUnload } from 'react-router-dom';
import {
  Link2,
  Maximize2,
  Minimize2,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { AdminHeader } from '../../components/admin/AdminHeader';
import { AdminNav } from '../../components/admin/AdminNav';
import { AdminLoginCard } from '../../components/admin/AdminLoginCard';
import { LegalDocumentContent } from '../../components/LegalDocumentContent';
import { RichLegalTextEditor } from '../../components/RichLegalTextEditor';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { db } from '../../firebase';
import { getDefaultLegalDocument } from '../../data/legalDocuments';
import { sanitizeLegalHtml } from '../../utils/legalContent';

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const addDays = (date, days) => new Date(date.getTime() + days * 86400000);

const diffDays = (start, end) =>
  Math.round((end.getTime() - start.getTime()) / 86400000);

const formatHeaderDate = (date) =>
  date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });

const WEEKDAY_SHORT_LABELS = ['V', 'H', 'K', 'Sze', 'Cs', 'P', 'Szo'];
const formatWeekdayShort = (date) => WEEKDAY_SHORT_LABELS[date.getDay()] || '';

const formatDateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const UNCATEGORIZED_LABEL = 'Kategória nélkül';
const ADMIN_CATEGORY_LABEL = 'Adminisztráció';
const PROJECT_TABS = [
  { id: 'company', label: 'Céginfó' },
  { id: 'gantt', label: 'Gantt diagram' },
  { id: 'notes', label: 'Jegyzetek' },
  { id: 'tasks', label: 'Feladatok' },
  { id: 'terms', label: 'ÁSZF' },
  { id: 'privacy', label: 'Adatvédelmi' },
  { id: 'withdrawal', label: 'Elállási jog' },
];
const LEGAL_DOCUMENT_IDS = ['terms', 'privacy', 'withdrawal'];
const createDefaultLegalDocuments = () => ({
  terms: getDefaultLegalDocument('terms'),
  privacy: getDefaultLegalDocument('privacy'),
  withdrawal: getDefaultLegalDocument('withdrawal'),
});
const EMPTY_COMPANY_INFO = {
  taxNumber: '',
  bankAccountNumber: '',
  registryNumber: '',
  registrationNumber: '',
  headquarters: '',
  phone: '',
  email: '',
};
const createEmptyCompanyInfo = () => ({ ...EMPTY_COMPANY_INFO });

const formatFirebaseError = (fallback, error) => {
  const details = error?.code || error?.message;
  return details ? `${fallback} (${details})` : fallback;
};

const isValidNoteLinkUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const renderNoteContent = (content) => {
  const text = String(content || '');
  const parts = [];
  const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={`${match.index}-${match[2]}`}
        href={match[2]}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-[#0F3D2C] underline underline-offset-2 hover:text-[#154532]"
      >
        {match[1]}
      </a>,
    );
    lastIndex = linkPattern.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const parseNoteContentForEditing = (content) => {
  const text = String(content || '');
  const links = [];
  const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
  let plainText = '';
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    plainText += text.slice(lastIndex, match.index);
    const start = plainText.length;
    plainText += match[1];
    links.push({
      start,
      end: plainText.length,
      url: match[2],
    });
    lastIndex = linkPattern.lastIndex;
  }

  plainText += text.slice(lastIndex);
  return { text: plainText, links };
};

const serializeEditableNoteContent = (text, links) => {
  const cleanText = String(text || '');
  const safeLinks = [...links]
    .filter(
      (link) =>
        Number.isInteger(link.start) &&
        Number.isInteger(link.end) &&
        link.start >= 0 &&
        link.end > link.start &&
        link.end <= cleanText.length &&
        isValidNoteLinkUrl(link.url),
    )
    .sort((a, b) => a.start - b.start);

  let result = '';
  let lastIndex = 0;

  safeLinks.forEach((link) => {
    if (link.start < lastIndex) return;
    result += cleanText.slice(lastIndex, link.start);
    result += `[${cleanText.slice(link.start, link.end)}](${link.url})`;
    lastIndex = link.end;
  });

  result += cleanText.slice(lastIndex);
  return result;
};

const reconcileNoteLinksAfterTextChange = (previousText, nextText, links) => {
  if (previousText === nextText) return links;

  let prefixLength = 0;
  const minLength = Math.min(previousText.length, nextText.length);
  while (
    prefixLength < minLength &&
    previousText[prefixLength] === nextText[prefixLength]
  ) {
    prefixLength += 1;
  }

  let suffixLength = 0;
  while (
    suffixLength < previousText.length - prefixLength &&
    suffixLength < nextText.length - prefixLength &&
    previousText[previousText.length - 1 - suffixLength] ===
      nextText[nextText.length - 1 - suffixLength]
  ) {
    suffixLength += 1;
  }

  const oldChangeEnd = previousText.length - suffixLength;
  const delta = nextText.length - previousText.length;

  return links
    .map((link) => {
      if (link.end <= prefixLength) return link;
      if (link.start >= oldChangeEnd) {
        return {
          ...link,
          start: link.start + delta,
          end: link.end + delta,
        };
      }
      return null;
    })
    .filter(Boolean);
};

const getLinkAtSelection = (links, selectionStart, selectionEnd = selectionStart) =>
  links.find((link) => {
    const selectionHasRange = selectionEnd > selectionStart;
    if (selectionHasRange) {
      return selectionStart < link.end && selectionEnd > link.start;
    }
    return selectionStart >= link.start && selectionStart <= link.end;
  }) || null;

const normalizeCategory = (value) => {
  if (!value || typeof value !== 'string') return UNCATEGORIZED_LABEL;
  const trimmed = value.trim();
  return trimmed ? trimmed : UNCATEGORIZED_LABEL;
};

const normalizeTaskType = (value) => {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim().toUpperCase();
  return trimmed === 'TASK' || trimmed === 'GANTT' ? trimmed : '';
};

const getTaskType = (task) => {
  const normalized = normalizeTaskType(task?.type);
  if (normalized) return normalized;
  if (task?.startDate || task?.endDate) return 'GANTT';
  if (task?.dueDate) return 'TASK';
  return 'GANTT';
};

const normalizeProgress = (value) => Math.min(100, Math.max(0, Number(value || 0)));
const GANTT_PROGRESS_STEPS = [0, 25, 50, 75, 90, 100];

const GANTT_CATEGORY_COLORS = ['#D9A7B5', '#AFCBE6', '#C9C2E8', '#9CCBC8'];
const ADMIN_CATEGORY_COLOR = '#9CCBC8';

const buildCategoryColorMap = (categories) => {
  const map = new Map();
  const reservedColor = GANTT_CATEGORY_COLORS[0];
  map.set(UNCATEGORIZED_LABEL, reservedColor);
  map.set(ADMIN_CATEGORY_LABEL, ADMIN_CATEGORY_COLOR);
  const availableColors = GANTT_CATEGORY_COLORS.filter(
    (color) => color !== reservedColor && color !== ADMIN_CATEGORY_COLOR,
  );
  let colorIndex = 0;
  categories.forEach((category) => {
    if (map.has(category)) return;
    map.set(category, availableColors[colorIndex % availableColors.length]);
    colorIndex += 1;
  });
  return map;
};

export default function AdminProjects() {
  const { user, loading } = useAdminAuth();
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [legalDocuments, setLegalDocuments] = useState(createDefaultLegalDocuments);
  const [legalDrafts, setLegalDrafts] = useState(createDefaultLegalDocuments);
  const [loadingLegalDocuments, setLoadingLegalDocuments] = useState(true);
  const [legalDocumentStatus, setLegalDocumentStatus] = useState({});
  const [savingLegalDocumentId, setSavingLegalDocumentId] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(createEmptyCompanyInfo);
  const [companyInfoDraft, setCompanyInfoDraft] = useState(createEmptyCompanyInfo);
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(true);
  const [savingCompanyInfo, setSavingCompanyInfo] = useState(false);
  const [companyInfoStatus, setCompanyInfoStatus] = useState(null);
  const [activeProjectView, setActiveProjectView] = useState('company');
  const [error, setError] = useState('');
  const [notesError, setNotesError] = useState('');
  const [form, setForm] = useState({
    title: '',
    type: 'GANTT',
    startDate: '',
    endDate: '',
    dueDate: '',
    owner: '',
    category: '',
    comment: '',
    progress: '0',
  });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const categoriesInitializedRef = useRef(false);
  const ganttScrollRef = useRef(null);
  const ganttAutoScrollRef = useRef(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showPendingTasksOnly, setShowPendingTasksOnly] = useState(true);
  const [selectedGanttTaskId, setSelectedGanttTaskId] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [moveState, setMoveState] = useState(null);
  const [updatingProgressTaskId, setUpdatingProgressTaskId] = useState(null);
  const [noteTitleDraft, setNoteTitleDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteTitle, setEditingNoteTitle] = useState('');
  const [editingNoteText, setEditingNoteText] = useState('');
  const [editingNoteLinks, setEditingNoteLinks] = useState([]);
  const [editingNoteLinkUrl, setEditingNoteLinkUrl] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [updatingNoteId, setUpdatingNoteId] = useState(null);
  const resizeSessionRef = useRef(null);
  const moveSessionRef = useRef(null);
  const resizePreviewRef = useRef(null);
  const editingNoteTextRef = useRef(null);
  const legalDocumentsRef = useRef(createDefaultLegalDocuments());
  const companyInfoRef = useRef(createEmptyCompanyInfo());

  const hasUnsavedLegalDocuments = useMemo(
    () => LEGAL_DOCUMENT_IDS.some(
      (documentId) => JSON.stringify(legalDrafts[documentId]) !== JSON.stringify(legalDocuments[documentId]),
    ),
    [legalDocuments, legalDrafts],
  );
  const hasUnsavedCompanyInfo = useMemo(
    () => JSON.stringify(companyInfoDraft) !== JSON.stringify(companyInfo),
    [companyInfo, companyInfoDraft],
  );
  const hasUnsavedProjectDetails = hasUnsavedLegalDocuments || hasUnsavedCompanyInfo;

  unstable_usePrompt({
    when: hasUnsavedProjectDetails,
    message: 'Nem mentett módosítások vannak a projektadatokban. Biztosan elhagyod az oldalt?',
  });

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasUnsavedProjectDetails) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [hasUnsavedProjectDetails],
    ),
  );

  useEffect(() => {
    if (!user) {
      setTasks([]);
      setLoadingTasks(false);
      return undefined;
    }
    setLoadingTasks(true);
    const q = query(collection(db, 'projectTasks'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setTasks(next);
        setLoadingTasks(false);
        setError('');
      },
      () => {
        setTasks([]);
        setLoadingTasks(false);
        setError('Nem sikerült betölteni a feladatokat.');
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNotes([]);
      setLoadingNotes(false);
      setNotesError('');
      return undefined;
    }
    setLoadingNotes(true);
    const q = query(collection(db, 'projectNotes'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setNotes(next);
        setLoadingNotes(false);
        setNotesError('');
      },
      (snapshotError) => {
        console.error('Project notes load failed:', snapshotError);
        setNotes([]);
        setLoadingNotes(false);
        setNotesError(formatFirebaseError('Nem sikerült betölteni a jegyzeteket.', snapshotError));
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      const defaults = createDefaultLegalDocuments();
      legalDocumentsRef.current = defaults;
      setLegalDocuments(defaults);
      setLegalDrafts(defaults);
      setLoadingLegalDocuments(false);
      setLegalDocumentStatus({});
      return undefined;
    }

    setLoadingLegalDocuments(true);
    const loadedDocumentIds = new Set();
    const unsubscribes = LEGAL_DOCUMENT_IDS.map((documentId) => onSnapshot(
      doc(db, 'legalDocuments', documentId),
      (snapshot) => {
        const fallback = getDefaultLegalDocument(documentId);
        const data = snapshot.exists() ? snapshot.data() : {};
        const nextDocument = {
          title: String(data.title || fallback.title),
          content: typeof data.content === 'string' ? data.content : fallback.content,
          fileUrl: typeof data.fileUrl === 'string'
            ? data.fileUrl
            : (typeof data.pdfUrl === 'string' ? data.pdfUrl : fallback.fileUrl),
          fileName: typeof data.fileName === 'string'
            ? data.fileName
            : (typeof data.pdfName === 'string' ? data.pdfName : fallback.fileName),
          fileType: typeof data.fileType === 'string'
            ? data.fileType
            : (data.pdfUrl ? 'pdf' : fallback.fileType),
        };
        const previousDocument = legalDocumentsRef.current[documentId];

        setLegalDrafts((currentDrafts) => {
          const isDirty =
            JSON.stringify(currentDrafts[documentId]) !== JSON.stringify(previousDocument);
          return {
            ...currentDrafts,
            [documentId]: isDirty ? currentDrafts[documentId] : nextDocument,
          };
        });
        legalDocumentsRef.current = {
          ...legalDocumentsRef.current,
          [documentId]: nextDocument,
        };
        setLegalDocuments((currentDocuments) => ({
          ...currentDocuments,
          [documentId]: nextDocument,
        }));
        setLegalDocumentStatus((currentStatus) => {
          if (currentStatus[documentId]?.source !== 'load') return currentStatus;
          return { ...currentStatus, [documentId]: null };
        });

        loadedDocumentIds.add(documentId);
        if (loadedDocumentIds.size === LEGAL_DOCUMENT_IDS.length) {
          setLoadingLegalDocuments(false);
        }
      },
      (snapshotError) => {
        console.error(`Legal document load failed (${documentId}):`, snapshotError);
        loadedDocumentIds.add(documentId);
        setLoadingLegalDocuments(false);
        setLegalDocumentStatus((currentStatus) => ({
          ...currentStatus,
          [documentId]: {
            type: 'error',
            source: 'load',
            text: formatFirebaseError('Nem sikerült betölteni a dokumentumot.', snapshotError),
          },
        }));
      },
    ));

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [user]);

  useEffect(() => {
    if (!user) {
      const emptyCompanyInfo = createEmptyCompanyInfo();
      companyInfoRef.current = emptyCompanyInfo;
      setCompanyInfo(emptyCompanyInfo);
      setCompanyInfoDraft(emptyCompanyInfo);
      setLoadingCompanyInfo(false);
      setCompanyInfoStatus(null);
      return undefined;
    }

    setLoadingCompanyInfo(true);
    const unsubscribe = onSnapshot(
      doc(db, 'companyInfo', 'main'),
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        const nextCompanyInfo = Object.keys(EMPTY_COMPANY_INFO).reduce(
          (result, field) => ({ ...result, [field]: String(data[field] || '') }),
          {},
        );
        const previousCompanyInfo = companyInfoRef.current;
        setCompanyInfoDraft((currentDraft) => (
          JSON.stringify(currentDraft) !== JSON.stringify(previousCompanyInfo)
            ? currentDraft
            : nextCompanyInfo
        ));
        companyInfoRef.current = nextCompanyInfo;
        setCompanyInfo(nextCompanyInfo);
        setLoadingCompanyInfo(false);
      },
      (snapshotError) => {
        console.error('Company info load failed:', snapshotError);
        setLoadingCompanyInfo(false);
        setCompanyInfoStatus({
          type: 'error',
          text: formatFirebaseError('Nem sikerült betölteni a cégadatokat.', snapshotError),
        });
      },
    );

    return () => unsubscribe();
  }, [user]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompanyInfoChange = (event) => {
    const { name, value } = event.target;
    setCompanyInfoDraft((current) => ({ ...current, [name]: value }));
    setCompanyInfoStatus(null);
  };

  const handleDiscardCompanyInfoChanges = () => {
    setCompanyInfoDraft({ ...companyInfo });
    setCompanyInfoStatus(null);
  };

  const handleSaveCompanyInfo = async (event) => {
    event.preventDefault();
    const normalizedCompanyInfo = Object.keys(EMPTY_COMPANY_INFO).reduce(
      (result, field) => ({ ...result, [field]: String(companyInfoDraft[field] || '').trim() }),
      {},
    );

    if (
      normalizedCompanyInfo.email
      && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedCompanyInfo.email)
    ) {
      setCompanyInfoStatus({ type: 'error', text: 'Adj meg érvényes email címet.' });
      return;
    }

    setSavingCompanyInfo(true);
    setCompanyInfoStatus(null);
    try {
      await setDoc(
        doc(db, 'companyInfo', 'main'),
        {
          ...normalizedCompanyInfo,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      companyInfoRef.current = normalizedCompanyInfo;
      setCompanyInfo(normalizedCompanyInfo);
      setCompanyInfoDraft(normalizedCompanyInfo);
      setCompanyInfoStatus({ type: 'success', text: 'A cégadatok mentése sikerült.' });
    } catch (saveError) {
      setCompanyInfoStatus({
        type: 'error',
        text: formatFirebaseError('Nem sikerült elmenteni a cégadatokat.', saveError),
      });
    } finally {
      setSavingCompanyInfo(false);
    }
  };

  const handleLegalDocumentChange = (documentId, field, value) => {
    setLegalDrafts((current) => ({
      ...current,
      [documentId]: {
        ...current[documentId],
        [field]: value,
      },
    }));
    setLegalDocumentStatus((current) => ({ ...current, [documentId]: null }));
  };

  const handleDiscardLegalDocumentChanges = (documentId) => {
    setLegalDrafts((current) => ({
      ...current,
      [documentId]: { ...legalDocuments[documentId] },
    }));
    setLegalDocumentStatus((current) => ({ ...current, [documentId]: null }));
  };

  const handleSaveLegalDocument = async (documentId) => {
    const draft = legalDrafts[documentId];
    const title = String(draft?.title || '').trim();
    const rawContent = String(draft?.content || '').trim();
    const usesRichText = documentId === 'terms' || documentId === 'privacy';
    const content = usesRichText ? sanitizeLegalHtml(rawContent) : rawContent;
    const fileUrl = documentId === 'terms' ? '' : String(draft?.fileUrl || '').trim();
    const fileName = documentId === 'terms' ? '' : String(draft?.fileName || '').trim();
    const fileType = documentId === 'terms' ? '' : String(draft?.fileType || '').trim();
    const hasReadableContent = usesRichText
      ? content.replace(/<[^>]*>/g, '').replaceAll('&nbsp;', ' ').trim().length > 0
      : Boolean(content);

    if (!title || !hasReadableContent) {
      setLegalDocumentStatus((current) => ({
        ...current,
        [documentId]: {
          type: 'error',
          text: 'A cím és a dokumentum szövege kötelező.',
        },
      }));
      return;
    }

    setSavingLegalDocumentId(documentId);
    setLegalDocumentStatus((current) => ({ ...current, [documentId]: null }));
    try {
      await setDoc(
        doc(db, 'legalDocuments', documentId),
        {
          title,
          content,
          fileUrl,
          fileName,
          fileType,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      const savedDocument = { title, content, fileUrl, fileName, fileType };
      legalDocumentsRef.current = {
        ...legalDocumentsRef.current,
        [documentId]: savedDocument,
      };
      setLegalDocuments((current) => ({ ...current, [documentId]: savedDocument }));
      setLegalDrafts((current) => ({ ...current, [documentId]: savedDocument }));
      setLegalDocumentStatus((current) => ({
        ...current,
        [documentId]: { type: 'success', text: 'A dokumentum mentve és publikálva.' },
      }));
    } catch (saveError) {
      setLegalDocumentStatus((current) => ({
        ...current,
        [documentId]: {
          type: 'error',
          text: formatFirebaseError('Nem sikerült elmenteni a dokumentumot.', saveError),
        },
      }));
    } finally {
      setSavingLegalDocumentId(null);
    }
  };

  const formatTimestamp = (value) => {
    if (!value) return '—';
    if (typeof value === 'object' && typeof value.toDate === 'function') {
      return value.toDate().toLocaleString('hu-HU');
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('hu-HU');
  };

  const handleAddTask = async (event) => {
    event.preventDefault();
    setError('');

    const title = form.title.trim();
    const type = normalizeTaskType(form.type) || 'GANTT';
    const isGantt = type === 'GANTT';
    const owner = form.owner.trim();
    const startDate = form.startDate;
    const endDate = form.endDate;
    const dueDate = form.dueDate;
    const rawCategory = form.category.trim();
    if (!rawCategory) {
      setError('A kategória kötelező.');
      return;
    }
    const category = rawCategory;
    const comment = form.comment.trim();
    const progress = normalizeProgress(form.progress);

    if (!title || (isGantt ? !startDate || !endDate : !dueDate)) {
      setError(
        isGantt
          ? 'A feladat neve, kezdete és vége kötelező.'
          : 'A feladat neve és határideje kötelező.',
      );
      return;
    }

    if (isGantt) {
      const start = toDate(startDate);
      const end = toDate(endDate);
      if (!start || !end || end < start) {
        setError('A feladat vége nem lehet korábbi, mint a kezdete.');
        return;
      }
    } else {
      const due = toDate(dueDate);
      if (!due) {
        setError('Érvénytelen határidő.');
        return;
      }
    }

    try {
      const basePayload = {
        title,
        owner,
        category,
        progress,
        type,
      };
      if (isGantt && editingTaskId) {
        basePayload.comment = deleteField();
      } else if (!isGantt) {
        basePayload.comment = comment;
      }

      if (editingTaskId) {
        const updatePayload = {
          ...basePayload,
          updatedAt: serverTimestamp(),
        };
        if (isGantt) {
          updatePayload.startDate = startDate;
          updatePayload.endDate = endDate;
          updatePayload.dueDate = deleteField();
        } else {
          updatePayload.dueDate = dueDate;
          updatePayload.startDate = deleteField();
          updatePayload.endDate = deleteField();
        }
        await updateDoc(doc(db, 'projectTasks', editingTaskId), updatePayload);
        setEditingTaskId(null);
      } else {
        const addPayload = {
          ...basePayload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        if (isGantt) {
          addPayload.startDate = startDate;
          addPayload.endDate = endDate;
        } else {
          addPayload.dueDate = dueDate;
          addPayload.comment = comment;
          addPayload.completed = false;
        }
        await addDoc(collection(db, 'projectTasks'), addPayload);
      }
      setIsTaskFormOpen(false);
      setForm({
        title: '',
        type,
        startDate: '',
        endDate: '',
        dueDate: '',
        owner: '',
        category: '',
        comment: '',
        progress: '0',
      });
      if (isGantt) {
        setSelectedCategories((prev) => {
          const next = new Set(prev);
          next.add(category);
          return Array.from(next);
        });
      }
    } catch {
      setError('Nem sikerült elmenteni a feladatot.');
    }
  };

  const handleStartEditTask = (task) => {
    const type = getTaskType(task);
    setActiveProjectView('notes-tasks');
    setEditingTaskId(task.id);
    setIsTaskFormOpen(true);
    setError('');
    setForm({
      title: task.title || '',
      type,
      startDate: task.startDate || '',
      endDate: task.endDate || '',
      dueDate: task.dueDate || '',
      owner: task.owner || '',
      category: task.category || '',
      comment: task.comment || '',
      progress: String(task.progress ?? 0),
    });
    if (type === 'GANTT') {
      const normalized = normalizeCategory(task.category);
      setSelectedCategories((prev) => {
        const next = new Set(prev);
        next.add(normalized);
        return Array.from(next);
      });
    }
  };

  const handleCancelEditTask = () => {
    setEditingTaskId(null);
    setIsTaskFormOpen(false);
    setForm({
      title: '',
      type: 'GANTT',
      startDate: '',
      endDate: '',
      dueDate: '',
      owner: '',
      category: '',
      comment: '',
      progress: '0',
    });
    setError('');
  };

  const handleOpenNewTaskForm = () => {
    setActiveProjectView('notes-tasks');
    setEditingTaskId(null);
    setIsTaskFormOpen(true);
    setError('');
    setForm({
      title: '',
      type: 'TASK',
      startDate: '',
      endDate: '',
      dueDate: '',
      owner: '',
      category: '',
      comment: '',
      progress: '0',
    });
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm('Biztosan törlöd ezt a feladatot?')) return;
    try {
      await deleteDoc(doc(db, 'projectTasks', id));
    } catch {
      setError('Nem sikerült törölni a feladatot.');
    }
  };

  const handleToggleTaskDone = async (taskId, nextValue) => {
    try {
      await updateDoc(doc(db, 'projectTasks', taskId), {
        completed: nextValue,
        progress: nextValue ? 100 : 0,
        updatedAt: serverTimestamp(),
      });
    } catch {
      setError('Nem sikerült frissíteni a task állapotát.');
    }
  };

  const handleAddNote = async (event) => {
    event.preventDefault();
    const title = noteTitleDraft.trim();
    const content = noteDraft.trim();
    if (!title) {
      setNotesError('A jegyzet címe kötelező.');
      return;
    }
    if (!content) {
      setNotesError('A jegyzet szövege kötelező.');
      return;
    }

    setSavingNote(true);
    setNotesError('');
    try {
      await addDoc(collection(db, 'projectNotes'), {
        title,
        content,
        createdBy: user?.uid || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNoteTitleDraft('');
      setNoteDraft('');
    } catch (saveError) {
      console.error('Project note save failed:', saveError);
      setNotesError(formatFirebaseError('Nem sikerült elmenteni a jegyzetet.', saveError));
    } finally {
      setSavingNote(false);
    }
  };

  const handleStartEditNote = (note) => {
    const editableContent = parseNoteContentForEditing(note?.content);
    setEditingNoteId(note.id);
    setEditingNoteTitle(String(note?.title || ''));
    setEditingNoteText(editableContent.text);
    setEditingNoteLinks(editableContent.links);
    setEditingNoteLinkUrl('');
    setNotesError('');
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteTitle('');
    setEditingNoteText('');
    setEditingNoteLinks([]);
    setEditingNoteLinkUrl('');
  };

  const handleEditingNoteTextChange = (event) => {
    const nextText = event.target.value;
    setEditingNoteLinks((currentLinks) =>
      reconcileNoteLinksAfterTextChange(editingNoteText, nextText, currentLinks),
    );
    setEditingNoteText(nextText);
  };

  const handleEditingNoteSelectionChange = (event) => {
    const textarea = event.currentTarget;
    const link = getLinkAtSelection(
      editingNoteLinks,
      textarea.selectionStart,
      textarea.selectionEnd,
    );

    if (!link) {
      setEditingNoteLinkUrl('');
    }
  };

  const handleEditingNoteDoubleClick = (event) => {
    const textarea = event.currentTarget;
    const link = getLinkAtSelection(
      editingNoteLinks,
      textarea.selectionStart,
      textarea.selectionEnd,
    );

    if (!link) return;

    setEditingNoteLinkUrl(link.url);
    setNotesError('');

    window.requestAnimationFrame(() => {
      if (!editingNoteTextRef.current) return;
      editingNoteTextRef.current.focus();
      editingNoteTextRef.current.setSelectionRange(link.start, link.end);
    });
  };

  const handleApplyEditingNoteLink = () => {
    const url = editingNoteLinkUrl.trim();
    if (!url) {
      setNotesError('Adj meg egy linket.');
      return;
    }
    if (!isValidNoteLinkUrl(url)) {
      setNotesError('A linknek http:// vagy https:// címmel kell kezdődnie.');
      return;
    }

    const textarea = editingNoteTextRef.current;
    const selectionStart = textarea?.selectionStart ?? -1;
    const selectionEnd = textarea?.selectionEnd ?? -1;
    if (selectionStart < 0 || selectionEnd <= selectionStart) {
      setNotesError('Jelöld ki a linkelendő szót vagy szöveget a jegyzetben.');
      return;
    }

    const selectedText = editingNoteText.slice(selectionStart, selectionEnd);
    const nextLinks = editingNoteLinks.filter(
      (link) => selectionEnd <= link.start || selectionStart >= link.end,
    );
    nextLinks.push({ start: selectionStart, end: selectionEnd, url });
    setEditingNoteLinks(nextLinks.sort((a, b) => a.start - b.start));
    setEditingNoteLinkUrl('');
    setNotesError('');

    window.requestAnimationFrame(() => {
      if (!editingNoteTextRef.current) return;
      editingNoteTextRef.current.focus();
      editingNoteTextRef.current.setSelectionRange(
        selectionStart,
        selectionStart + selectedText.length,
      );
    });
  };

  const handleOpenEditingNoteLink = () => {
    const textarea = editingNoteTextRef.current;
    const link = getLinkAtSelection(
      editingNoteLinks,
      textarea?.selectionStart ?? -1,
      textarea?.selectionEnd ?? -1,
    );

    if (!link) {
      setNotesError('Jelöld ki vagy kattints duplán egy linkelt szóra.');
      return;
    }

    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const handleSaveEditedNote = async (noteId) => {
    const title = editingNoteTitle.trim();
    const content = serializeEditableNoteContent(editingNoteText, editingNoteLinks).trim();
    if (!title) {
      setNotesError('A jegyzet címe kötelező.');
      return;
    }
    if (!content) {
      setNotesError('A jegyzet szövege kötelező.');
      return;
    }

    setUpdatingNoteId(noteId);
    setNotesError('');
    try {
      await updateDoc(doc(db, 'projectNotes', noteId), {
        title,
        content,
        updatedAt: serverTimestamp(),
      });
      setEditingNoteId(null);
      setEditingNoteTitle('');
      setEditingNoteText('');
      setEditingNoteLinks([]);
      setEditingNoteLinkUrl('');
    } catch (saveError) {
      console.error('Project note update failed:', saveError);
      setNotesError(formatFirebaseError('Nem sikerült menteni a jegyzet módosítását.', saveError));
    } finally {
      setUpdatingNoteId(null);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Biztosan törlöd ezt a jegyzetet?')) return;

    setUpdatingNoteId(noteId);
    setNotesError('');
    try {
      await deleteDoc(doc(db, 'projectNotes', noteId));
      if (editingNoteId === noteId) {
        setEditingNoteId(null);
        setEditingNoteTitle('');
        setEditingNoteText('');
        setEditingNoteLinks([]);
        setEditingNoteLinkUrl('');
      }
    } catch (deleteError) {
      console.error('Project note delete failed:', deleteError);
      setNotesError(formatFirebaseError('Nem sikerült törölni a jegyzetet.', deleteError));
    } finally {
      setUpdatingNoteId(null);
    }
  };

  const handleSetGanttTaskProgress = async (taskId, nextProgress) => {
    const normalizedProgress = normalizeProgress(nextProgress);
    if (!taskId) return;
    if (updatingProgressTaskId === taskId) return;
    setUpdatingProgressTaskId(taskId);
    setError('');
    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? {
              ...task,
              progress: normalizedProgress,
            }
          : task,
      ),
    );
    try {
      await updateDoc(doc(db, 'projectTasks', taskId), {
        progress: normalizedProgress,
        updatedAt: serverTimestamp(),
      });
    } catch {
      setError('Nem sikerült frissíteni a Gantt feladat készültségét.');
    } finally {
      setUpdatingProgressTaskId(null);
    }
  };

  const ganttTasks = useMemo(
    () =>
      tasks
        .map((task) => ({
          ...task,
          type: getTaskType(task),
          category: normalizeCategory(task.category),
          start: toDate(task.startDate),
          end: toDate(task.endDate),
          progress: normalizeProgress(task.progress),
          completed: Boolean(task.completed),
        }))
        .filter((task) => task.type === 'GANTT' && task.start && task.end)
        .sort((a, b) => a.start - b.start),
    [tasks],
  );

  const ganttTasksWithPreview = useMemo(() => {
    if (!resizePreview) return ganttTasks;
    return ganttTasks.map((task) =>
      task.id === resizePreview.taskId
        ? {
            ...task,
            start: resizePreview.start,
            end: resizePreview.end,
          }
        : task,
    );
  }, [ganttTasks, resizePreview]);

  const allTaskItems = useMemo(() => {
    const items = tasks
      .map((task) => ({
        ...task,
        type: getTaskType(task),
        category: normalizeCategory(task.category),
        progress: normalizeProgress(task.progress),
        completed: Boolean(task.completed),
      }))
      .filter((task) => task.type === 'TASK');

    return items.sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      const aDate = a.dueDate || '';
      const bDate = b.dueDate || '';
      if (aDate && bDate) return aDate.localeCompare(bDate);
      if (aDate) return -1;
      if (bDate) return 1;
      return (a.title || '').localeCompare(b.title || '', 'hu');
    });
  }, [tasks]);

  const taskItems = useMemo(
    () => (showPendingTasksOnly ? allTaskItems.filter((task) => !task.completed) : allTaskItems),
    [allTaskItems, showPendingTasksOnly],
  );

  const categorySuggestions = useMemo(() => {
    const unique = new Set();
    tasks.forEach((task) => {
      unique.add(normalizeCategory(task.category));
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'hu'));
  }, [tasks]);

  const ownerSuggestions = useMemo(() => {
    const unique = new Set();
    tasks.forEach((task) => {
      const owner = String(task?.owner || '').trim();
      if (owner) unique.add(owner);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'hu'));
  }, [tasks]);

  const categoryOptions = useMemo(() => {
    const unique = new Set();
    ganttTasks.forEach((task) => {
      unique.add(task.category);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'hu'));
  }, [ganttTasks]);

  const categoryColorMap = useMemo(
    () => buildCategoryColorMap(categoryOptions),
    [categoryOptions],
  );

  useEffect(() => {
    if (!selectedTaskId) return;
    if (!taskItems.some((task) => task.id === selectedTaskId)) {
      setSelectedTaskId(null);
    }
  }, [taskItems, selectedTaskId]);

  useEffect(() => {
    if (!selectedGanttTaskId) return;
    if (!ganttTasks.some((task) => task.id === selectedGanttTaskId)) {
      setSelectedGanttTaskId(null);
    }
  }, [ganttTasks, selectedGanttTaskId]);

  useEffect(() => {
    if (categoryOptions.length === 0) {
      setSelectedCategories([]);
      categoriesInitializedRef.current = false;
      return;
    }
    setSelectedCategories((prev) => {
      if (!categoriesInitializedRef.current) {
        categoriesInitializedRef.current = true;
        return categoryOptions;
      }
      const prevSet = new Set(prev);
      const next = categoryOptions.filter((category) => prevSet.has(category));
      return next;
    });
  }, [categoryOptions]);

  const ganttData = useMemo(() => {
    const filteredTasks =
      selectedCategories.length > 0
        ? ganttTasksWithPreview.filter((task) => selectedCategories.includes(task.category))
        : ganttTasksWithPreview;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const fallbackStart = todayStart;
    const fallbackEnd = addDays(todayStart, 14);

    const rangeStart =
      filteredTasks.length > 0
        ? filteredTasks.reduce(
            (min, task) => (task.start < min ? task.start : min),
            filteredTasks[0].start,
          )
        : fallbackStart;
    const rangeEnd =
      filteredTasks.length > 0
        ? filteredTasks.reduce(
            (max, task) => (task.end > max ? task.end : max),
            filteredTasks[0].end,
          )
        : fallbackEnd;
    const normalizedRangeStart = rangeStart < todayStart ? rangeStart : todayStart;
    const normalizedRangeEnd = rangeEnd > todayStart ? rangeEnd : todayStart;

    const totalDays = Math.max(1, diffDays(normalizedRangeStart, normalizedRangeEnd) + 1);
    const useWeeks = totalDays > 60;
    const columnWidth = useWeeks ? 72 : 28;
    const columns = useWeeks ? Math.ceil(totalDays / 7) : totalDays;

    const headers = Array.from({ length: columns }, (_, index) => {
      const dayOffset = useWeeks ? index * 7 : index;
      const date = addDays(normalizedRangeStart, dayOffset);
      const dateLabel = useWeeks
        ? formatHeaderDate(date)
        : date.getDate() === 1 || index === 0
          ? formatHeaderDate(date)
          : date.getDate().toString();
      return {
        key: formatDateValue(date),
        dateLabel,
        weekdayLabel: formatWeekdayShort(date),
      };
    });

    const timelineStyle = {
      backgroundImage:
        `repeating-linear-gradient(to right, rgba(15, 61, 44, 0.08), ` +
        `rgba(15, 61, 44, 0.08) 1px, transparent 1px, transparent ${columnWidth}px)`,
    };

    const todayOffset = diffDays(normalizedRangeStart, todayStart);
    const normalizedToday = useWeeks ? Math.floor(todayOffset / 7) : todayOffset;
    const todayIndex = normalizedToday >= 0 && normalizedToday < columns ? normalizedToday : null;
    const todayColumnStyle =
      todayIndex !== null
        ? {
            left: todayIndex * columnWidth,
            width: columnWidth,
          }
        : null;
    const todayLineStyle =
      todayIndex !== null
        ? {
            left: todayIndex * columnWidth + columnWidth / 2,
          }
        : null;

    const getBarStyle = (task) => {
      const startOffset = diffDays(normalizedRangeStart, task.start);
      const endOffset = diffDays(normalizedRangeStart, task.end);
      const normalizedStart = useWeeks ? Math.floor(startOffset / 7) : startOffset;
      const normalizedEnd = useWeeks ? Math.floor(endOffset / 7) : endOffset;
      const left = Math.max(0, normalizedStart) * columnWidth;
      const width = (Math.max(normalizedEnd, normalizedStart) - normalizedStart + 1) * columnWidth;
      return { left, width };
    };

    return {
      tasks: filteredTasks,
      headers,
      columns,
      columnWidth,
      timelineStyle,
      getBarStyle,
      useWeeks,
      todayStart,
      todayIndex,
      todayColumnStyle,
      todayLineStyle,
      todayLabel: formatHeaderDate(todayStart),
    };
  }, [ganttTasksWithPreview, selectedCategories]);

  useEffect(() => {
    if (ganttAutoScrollRef.current) return;
    if (!ganttScrollRef.current) return;
    if (ganttData.tasks.length === 0) return;
    if (ganttData.todayIndex === null) return;

    const container = ganttScrollRef.current;
    const leftColumnWidth = 220;
    const todayCenter =
      leftColumnWidth + ganttData.todayIndex * ganttData.columnWidth + ganttData.columnWidth / 2;
    const targetScrollLeft = Math.max(0, todayCenter - container.clientWidth / 2);

    const handle = requestAnimationFrame(() => {
      container.scrollLeft = targetScrollLeft;
      ganttAutoScrollRef.current = true;
    });

    return () => cancelAnimationFrame(handle);
  }, [ganttData.todayIndex, ganttData.columnWidth, ganttData.tasks.length]);

  const handleResizePointerDown = (event, task, edge) => {
    if (moveState) return;
    event.preventDefault();
    event.stopPropagation();

    const session = {
      taskId: task.id,
      edge,
      pointerStartX: event.clientX,
      initialStart: task.start,
      initialEnd: task.end,
      stepDays: ganttData.useWeeks ? 7 : 1,
      columnWidth: ganttData.columnWidth,
    };

    resizeSessionRef.current = session;
    const initialPreview = {
      taskId: task.id,
      start: task.start,
      end: task.end,
    };
    resizePreviewRef.current = initialPreview;
    setResizePreview(initialPreview);
    setResizeState({ taskId: task.id, edge });
    setSelectedGanttTaskId(task.id);
  };

  const handleMovePointerDown = (event, task) => {
    if (event.button !== 0) return;
    if (resizeState) return;
    if (moveState) return;
    event.preventDefault();
    event.stopPropagation();

    const session = {
      taskId: task.id,
      pointerStartX: event.clientX,
      initialStart: task.start,
      initialEnd: task.end,
      stepDays: ganttData.useWeeks ? 7 : 1,
      columnWidth: ganttData.columnWidth,
    };

    moveSessionRef.current = session;
    const initialPreview = {
      taskId: task.id,
      start: task.start,
      end: task.end,
    };
    resizePreviewRef.current = initialPreview;
    setResizePreview(initialPreview);
    setMoveState({ taskId: task.id });
    setSelectedGanttTaskId(task.id);
  };

  useEffect(() => {
    if (!resizeState) return undefined;

    const handlePointerMove = (event) => {
      const session = resizeSessionRef.current;
      if (!session) return;

      const deltaX = event.clientX - session.pointerStartX;
      const offsetUnits = Math.round(deltaX / session.columnWidth);
      const deltaDays = offsetUnits * session.stepDays;

      let nextStart = session.initialStart;
      let nextEnd = session.initialEnd;

      if (session.edge === 'start') {
        const candidateStart = addDays(session.initialStart, deltaDays);
        nextStart = candidateStart > session.initialEnd ? session.initialEnd : candidateStart;
      } else {
        const candidateEnd = addDays(session.initialEnd, deltaDays);
        nextEnd = candidateEnd < session.initialStart ? session.initialStart : candidateEnd;
      }

      const previousPreview = resizePreviewRef.current;
      if (
        previousPreview &&
        previousPreview.taskId === session.taskId &&
        previousPreview.start.getTime() === nextStart.getTime() &&
        previousPreview.end.getTime() === nextEnd.getTime()
      ) {
        return;
      }

      const nextPreview = {
        taskId: session.taskId,
        start: nextStart,
        end: nextEnd,
      };
      resizePreviewRef.current = nextPreview;
      setResizePreview(nextPreview);
    };

    const handlePointerUp = () => {
      const session = resizeSessionRef.current;
      if (!session) return;

      const preview = resizePreviewRef.current;
      const nextStart =
        preview && preview.taskId === session.taskId ? preview.start : session.initialStart;
      const nextEnd = preview && preview.taskId === session.taskId ? preview.end : session.initialEnd;

      const hasChange =
        nextStart.getTime() !== session.initialStart.getTime() ||
        nextEnd.getTime() !== session.initialEnd.getTime();

      resizeSessionRef.current = null;
      resizePreviewRef.current = null;
      setResizeState(null);
      setResizePreview(null);

      if (!hasChange) return;

      void (async () => {
        try {
          await updateDoc(doc(db, 'projectTasks', session.taskId), {
            startDate: formatDateValue(nextStart),
            endDate: formatDateValue(nextEnd),
            updatedAt: serverTimestamp(),
          });
        } catch {
          setError('Nem sikerült módosítani a Gantt feladat időtartamát.');
        }
      })();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [resizeState]);

  useEffect(() => {
    if (!moveState) return undefined;

    const handlePointerMove = (event) => {
      const session = moveSessionRef.current;
      if (!session) return;

      const deltaX = event.clientX - session.pointerStartX;
      const offsetUnits = Math.round(deltaX / session.columnWidth);
      const deltaDays = offsetUnits * session.stepDays;

      const nextStart = addDays(session.initialStart, deltaDays);
      const nextEnd = addDays(session.initialEnd, deltaDays);

      const previousPreview = resizePreviewRef.current;
      if (
        previousPreview &&
        previousPreview.taskId === session.taskId &&
        previousPreview.start.getTime() === nextStart.getTime() &&
        previousPreview.end.getTime() === nextEnd.getTime()
      ) {
        return;
      }

      const nextPreview = {
        taskId: session.taskId,
        start: nextStart,
        end: nextEnd,
      };
      resizePreviewRef.current = nextPreview;
      setResizePreview(nextPreview);
    };

    const handlePointerUp = () => {
      const session = moveSessionRef.current;
      if (!session) return;

      const preview = resizePreviewRef.current;
      const nextStart =
        preview && preview.taskId === session.taskId ? preview.start : session.initialStart;
      const nextEnd = preview && preview.taskId === session.taskId ? preview.end : session.initialEnd;

      const hasChange =
        nextStart.getTime() !== session.initialStart.getTime() ||
        nextEnd.getTime() !== session.initialEnd.getTime();

      moveSessionRef.current = null;
      resizePreviewRef.current = null;
      setMoveState(null);
      setResizePreview(null);

      if (!hasChange) return;

      void (async () => {
        try {
          await updateDoc(doc(db, 'projectTasks', session.taskId), {
            startDate: formatDateValue(nextStart),
            endDate: formatDateValue(nextEnd),
            updatedAt: serverTimestamp(),
          });
        } catch {
          setError('Nem sikerült áthelyezni a Gantt feladatot.');
        }
      })();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [moveState]);

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return Array.from(next);
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <div className="rounded-3xl border border-[#0F3D2C] p-8 md:p-10 bg-card/60">
              <p className="text-sm text-muted-foreground">Betöltés...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">Admin / Projekt</h1>
            <AdminLoginCard description="A projektekhez jelentkezz be Google fiókkal." />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="section-padding">
        <div className="container-zinga">
          <AdminHeader title="Admin / Projekt" />
          <AdminNav />

          <div className="mt-8 flex flex-wrap gap-2 border-b border-border/70">
            {PROJECT_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveProjectView(tab.id)}
                className={`border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                  activeProjectView === tab.id
                    ? 'border-[#0F3D2C] text-[#0F3D2C]'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeProjectView === 'company' ? (
            <CompanyInfoEditor
              companyInfo={companyInfoDraft}
              loading={loadingCompanyInfo}
              saving={savingCompanyInfo}
              isDirty={hasUnsavedCompanyInfo}
              status={companyInfoStatus}
              onChange={handleCompanyInfoChange}
              onSave={handleSaveCompanyInfo}
              onDiscard={handleDiscardCompanyInfoChanges}
            />
          ) : null}

          {activeProjectView === 'tasks' ? (
          <div className="mt-6 rounded-3xl border border-border/70 bg-card p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-xl font-semibold">Feladatok</h2>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#0F3D2C]"
                    checked={showPendingTasksOnly}
                    onChange={(event) => setShowPendingTasksOnly(event.target.checked)}
                  />
                  Csak el nem végzettek
                </label>
                <button
                  type="button"
                  onClick={handleOpenNewTaskForm}
                  className="btn-primary text-sm"
                >
                  + Új feladat
                </button>
              </div>
            </div>
            {isTaskFormOpen ? (
              <div className="mb-6 rounded-2xl border border-border/60 bg-background/60 p-4">
                <h3 className="mb-4 font-display text-lg font-semibold">
                  {editingTaskId ? 'Feladat szerkesztése' : 'Új feladat'}
                </h3>
                <form onSubmit={handleAddTask} className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium mb-1">Feladat neve</label>
                    <input
                      name="title"
                      value={form.title}
                      onChange={handleFormChange}
                      className="input-zinga"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Típus</label>
                    <select
                      name="type"
                      value={form.type}
                      onChange={handleFormChange}
                      className="input-zinga"
                    >
                      <option value="GANTT">GANTT</option>
                      <option value="TASK">TASK</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Kategória</label>
                    <input
                      name="category"
                      value={form.category}
                      onChange={handleFormChange}
                      className="input-zinga"
                      list="category-options"
                      placeholder="Pl. Beszerzés"
                    />
                    <datalist id="category-options">
                      {categorySuggestions.map((category) => (
                        <option key={category} value={category} />
                      ))}
                    </datalist>
                  </div>
                  {form.type === 'TASK' ? (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Határidő</label>
                      <input
                        type="date"
                        name="dueDate"
                        value={form.dueDate}
                        onChange={handleFormChange}
                        className="input-zinga"
                      />
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-medium mb-1">Kezdés</label>
                        <input
                          type="date"
                          name="startDate"
                          value={form.startDate}
                          onChange={handleFormChange}
                          className="input-zinga"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Befejezés</label>
                        <input
                          type="date"
                          name="endDate"
                          value={form.endDate}
                          onChange={handleFormChange}
                          className="input-zinga"
                        />
                      </div>
                    </>
                  )}
                  {form.type === 'TASK' ? (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Megjegyzés</label>
                      <input
                        name="comment"
                        value={form.comment}
                        onChange={handleFormChange}
                        className="input-zinga"
                        placeholder="Pl. egyeztetés szükséges"
                      />
                    </div>
                  ) : null}
                  <div>
                    <label className="block text-xs font-medium mb-1">Felelős</label>
                    <input
                      name="owner"
                      value={form.owner}
                      onChange={handleFormChange}
                      className="input-zinga"
                      list="owner-options"
                      placeholder="Pl. Anna"
                    />
                    <datalist id="owner-options">
                      {ownerSuggestions.map((owner) => (
                        <option key={owner} value={owner} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Készültség (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      name="progress"
                      value={form.progress}
                      onChange={handleFormChange}
                      className="input-zinga"
                    />
                  </div>
                  <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3">
                    {error && <span className="text-xs text-destructive">{error}</span>}
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={handleCancelEditTask}
                        className="btn-secondary text-sm"
                      >
                        Mégse
                      </button>
                      <button type="submit" className="btn-primary text-sm">
                        {editingTaskId ? 'Mentés' : '+ Feladat hozzáadása'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            ) : null}
            {error && !isTaskFormOpen ? (
              <div className="mb-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                {error}
              </div>
            ) : null}
            {loadingTasks ? (
              <p className="text-sm text-muted-foreground">Betöltés...</p>
            ) : taskItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {showPendingTasksOnly
                  ? 'Nincs el nem végzett feladat.'
                  : 'Nincs rögzített feladat.'}
              </p>
            ) : (
              <div className="grid gap-3 lg:grid-cols-2">
                {taskItems.map((task) => (
                  <div
                    key={task.id}
                    className={`rounded-2xl border bg-card/60 p-4 transition-colors cursor-pointer ${
                      selectedTaskId === task.id
                        ? 'border-[#0F3D2C]/40'
                        : 'border-border/60 hover:border-border'
                    }`}
                    onClick={() =>
                      setSelectedTaskId((prev) => (prev === task.id ? null : task.id))
                    }
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">{task.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {task.dueDate ? `Határidő: ${task.dueDate}` : 'Nincs határidő'}
                          {task.category ? ` · ${task.category}` : ''}
                        </p>
                        {task.owner ? (
                          <p className="text-xs text-muted-foreground">Felelős: {task.owner}</p>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          {task.progress ?? 0}%
                        </span>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleStartEditTask(task);
                          }}
                          className="btn-secondary text-xs px-2 py-1"
                          title="Szerkesztés"
                          aria-label="Szerkesztés"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteTask(task.id);
                          }}
                          className="btn-secondary text-xs text-destructive px-2 py-1"
                          title="Törlés"
                          aria-label="Törlés"
                        >
                          <Trash2 size={12} />
                        </button>
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#0F3D2C]"
                          checked={task.completed}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) =>
                            handleToggleTaskDone(task.id, event.target.checked)
                          }
                          title="Kész"
                          aria-label="Kész"
                        />
                      </div>
                    </div>
                    {selectedTaskId === task.id && task.comment ? (
                      <div className="mt-3 rounded-xl border border-border/50 bg-background/70 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Megjegyzés
                        </p>
                        <p className="text-xs text-muted-foreground">{task.comment}</p>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
          ) : null}

          {activeProjectView === 'notes' ? (
          <div className="mt-6 rounded-3xl border border-border/70 bg-card p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-xl font-semibold">Jegyzetek</h2>
              <span className="text-xs text-muted-foreground">Projekt jegyzetek</span>
            </div>
            <form onSubmit={handleAddNote} className="space-y-3">
              <div>
                <label htmlFor="project-note-title-input" className="block text-xs font-medium mb-1">
                  Jegyzet címe
                </label>
                <input
                  id="project-note-title-input"
                  value={noteTitleDraft}
                  onChange={(event) => setNoteTitleDraft(event.target.value)}
                  className="input-zinga"
                  placeholder="Pl. Beszállítói egyeztetés"
                />
              </div>
              <label htmlFor="project-note-input" className="block text-xs font-medium">
                Jegyzet szövege
              </label>
              <textarea
                id="project-note-input"
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                rows={4}
                placeholder="Írj egy új jegyzetet..."
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                {notesError ? (
                  <span className="text-xs text-destructive">{notesError}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    Több különálló jegyzetet is rögzíthetsz.
                  </span>
                )}
                <button
                  type="submit"
                  className="btn-primary text-sm"
                  disabled={savingNote}
                >
                  {savingNote ? 'Mentés...' : '+ Jegyzet hozzáadása'}
                </button>
              </div>
            </form>

            {loadingNotes ? (
              <p className="mt-4 text-sm text-muted-foreground">Jegyzetek betöltése...</p>
            ) : notes.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">Még nincs rögzített jegyzet.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {notes.map((note) => {
                  const isEditing = editingNoteId === note.id;
                  const isBusy = updatingNoteId === note.id;
                  return (
                    <div key={note.id} className="rounded-2xl border border-border/60 bg-background/60 p-4">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Frissítve: {formatTimestamp(note.updatedAt || note.createdAt)}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                onClick={handleCancelEditNote}
                                className="btn-secondary text-xs px-2 py-1"
                                disabled={isBusy}
                              >
                                Mégse
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleSaveEditedNote(note.id)}
                                className="btn-primary text-xs px-2 py-1"
                                disabled={isBusy}
                              >
                                {isBusy ? 'Mentés...' : 'Mentés'}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => handleStartEditNote(note)}
                                className="btn-secondary text-xs px-2 py-1"
                                disabled={isBusy}
                              >
                                Szerkesztés
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleDeleteNote(note.id)}
                                className="btn-secondary text-xs text-destructive px-2 py-1"
                                disabled={isBusy}
                              >
                                Törlés
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {isEditing ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium mb-1">Jegyzet címe</label>
                            <input
                              value={editingNoteTitle}
                              onChange={(event) => setEditingNoteTitle(event.target.value)}
                              className="input-zinga"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Jegyzet szövege</label>
                            <textarea
                              ref={editingNoteTextRef}
                              value={editingNoteText}
                              onChange={handleEditingNoteTextChange}
                              onSelect={handleEditingNoteSelectionChange}
                              onDoubleClick={handleEditingNoteDoubleClick}
                              className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                              rows={4}
                            />
                          </div>
                          <div className="flex flex-wrap items-end gap-2">
                            <div className="min-w-[220px] flex-1">
                              <label className="block text-xs font-medium mb-1">Link a kijelölt szóhoz</label>
                              <input
                                value={editingNoteLinkUrl}
                                onChange={(event) => setEditingNoteLinkUrl(event.target.value)}
                                className="input-zinga"
                                placeholder="https://..."
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleApplyEditingNoteLink}
                              className="btn-secondary inline-flex items-center gap-2 text-sm"
                            >
                              <Link2 size={14} />
                              Link hozzáadása
                            </button>
                            <button
                              type="button"
                              onClick={handleOpenEditingNoteLink}
                              className="btn-secondary inline-flex items-center gap-2 text-sm"
                            >
                              <Link2 size={14} />
                              Link megnyitása
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <h3 className="mb-2 text-sm font-semibold text-foreground">
                            {note.title || 'Cím nélküli jegyzet'}
                          </h3>
                          <p className="whitespace-pre-wrap text-sm text-foreground/90">
                            {renderNoteContent(note.content)}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          ) : null}

          {activeProjectView === 'terms' ? (
            <LegalDocumentEditor
              documentId="terms"
              label="ÁSZF"
              document={legalDrafts.terms}
              loading={loadingLegalDocuments}
              saving={savingLegalDocumentId === 'terms'}
              isDirty={JSON.stringify(legalDrafts.terms) !== JSON.stringify(legalDocuments.terms)}
              status={legalDocumentStatus.terms}
              onChange={handleLegalDocumentChange}
              onSave={handleSaveLegalDocument}
              onDiscard={handleDiscardLegalDocumentChanges}
            />
          ) : null}

          {activeProjectView === 'privacy' ? (
            <LegalDocumentEditor
              documentId="privacy"
              label="Adatvédelmi nyilatkozat"
              document={legalDrafts.privacy}
              loading={loadingLegalDocuments}
              saving={savingLegalDocumentId === 'privacy'}
              isDirty={JSON.stringify(legalDrafts.privacy) !== JSON.stringify(legalDocuments.privacy)}
              status={legalDocumentStatus.privacy}
              onChange={handleLegalDocumentChange}
              onSave={handleSaveLegalDocument}
              onDiscard={handleDiscardLegalDocumentChanges}
            />
          ) : null}

          {activeProjectView === 'withdrawal' ? (
            <LegalDocumentEditor
              documentId="withdrawal"
              label="Elállási jog"
              document={legalDrafts.withdrawal}
              loading={loadingLegalDocuments}
              saving={savingLegalDocumentId === 'withdrawal'}
              isDirty={JSON.stringify(legalDrafts.withdrawal) !== JSON.stringify(legalDocuments.withdrawal)}
              status={legalDocumentStatus.withdrawal}
              onChange={handleLegalDocumentChange}
              onSave={handleSaveLegalDocument}
              onDiscard={handleDiscardLegalDocumentChanges}
            />
          ) : null}

          {activeProjectView === 'gantt' ? (
          <div className="mt-6 rounded-3xl border border-border/70 bg-card p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-display text-xl font-semibold">Gantt nézet</h2>
              <p className="text-xs text-muted-foreground">
                {ganttData.useWeeks ? 'Heti bontás' : 'Napi bontás'}
              </p>
            </div>
            <p className="mb-4 text-xs text-muted-foreground">
              A sáv testét húzva áthelyezheted a feladatot, a széleit húzva az időtartamot módosíthatod.
            </p>
            {categoryOptions.length > 0 ? (
              <div className="mb-4 flex flex-wrap items-center gap-3 text-xs">
                <span className="text-muted-foreground">Kategóriák:</span>
                {categoryOptions.map((category) => (
                  <label key={category} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="accent-emerald-600"
                      checked={selectedCategories.includes(category)}
                      onChange={() => toggleCategory(category)}
                      style={
                        selectedCategories.includes(category)
                          ? {
                              accentColor:
                                categoryColorMap.get(category) || GANTT_CATEGORY_COLORS[0],
                            }
                          : undefined
                      }
                    />
                    <span>{category}</span>
                  </label>
                ))}
              </div>
            ) : null}
            {ganttTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Adj hozzá GANTT feladatot a Gantt diagramhoz.
              </p>
            ) : ganttData.tasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nincs megjelenített kategória.</p>
            ) : (
              <div className="max-h-[70vh] overflow-auto pr-1" ref={ganttScrollRef}>
                <div
                  className="min-w-max"
                  style={{
                    minWidth: 220 + ganttData.columns * ganttData.columnWidth + 64,
                  }}
                >
                  <div
                    className="sticky top-0 z-40 grid items-center text-[10px] uppercase tracking-wide text-muted-foreground bg-card/95 backdrop-blur-sm border-b border-border/40"
                    style={{
                      gridTemplateColumns: `220px repeat(${ganttData.columns}, ${ganttData.columnWidth}px) 64px`,
                    }}
                  >
                    <div className="sticky left-0 self-stretch z-20 pr-4 bg-card border-r border-border/40 flex items-center">
                      Feladat
                    </div>
                    {ganttData.headers.map((header, index) => (
                      <div
                        key={`${header.key}-${index}`}
                        className={`text-center py-1 ${
                          ganttData.todayIndex === index
                            ? 'bg-[#0F3D2C] text-[#F1E6D3] font-semibold'
                            : ''
                        }`}
                        aria-current={ganttData.todayIndex === index ? 'date' : undefined}
                        title={ganttData.todayIndex === index ? `Ma: ${ganttData.todayLabel}` : undefined}
                      >
                        <div className="leading-tight">
                          <span className="block">{header.dateLabel}</span>
                          <span
                            className={`block text-[9px] ${
                              ganttData.todayIndex === index ? 'text-[#F1E6D3]/90' : 'text-muted-foreground'
                            }`}
                          >
                            {header.weekdayLabel}
                          </span>
                          {ganttData.todayIndex === index ? (
                            <span className="block text-[9px] font-semibold text-[#F1E6D3]/90">
                              Ma
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                    <div className="sticky right-0 z-20 bg-transparent border-l border-border/40 text-center">
                      <span className="inline-flex items-center justify-center rounded-full bg-card/80 px-2 py-0.5">
                        Kész
                      </span>
                    </div>
                  </div>

                  {ganttData.tasks.map((task) => {
                    const bar = ganttData.getBarStyle(task);
                    const barWidth = Math.max(10, bar.width);
                    const progressWidth = Math.round((barWidth * task.progress) / 100);
                    const durationDays = diffDays(task.start, task.end) + 1;
                    const categoryColor =
                      categoryColorMap.get(task.category) || GANTT_CATEGORY_COLORS[0];
                    const isSelected = selectedGanttTaskId === task.id;
                    const isResizingTask = resizeState?.taskId === task.id;
                    const isMovingTask = moveState?.taskId === task.id;
                    const isOverdue = task.end < ganttData.todayStart;
                    const isDone = task.completed || task.progress >= 100;
                    const outlineColor = isDone
                      ? 'rgba(15, 61, 44, 0.95)'
                      : isOverdue
                        ? 'rgba(220, 38, 38, 0.95)'
                        : null;
                    const timelineWidth = ganttData.columns * ganttData.columnWidth;
                    const leftHandleLeft = Math.max(0, bar.left - 6);
                    const rightHandleLeft = Math.min(
                      Math.max(0, timelineWidth - 12),
                      bar.left + barWidth - 6,
                    );
                    const progressPopoverWidth = 174;
                    const progressPopoverLeft = Math.min(
                      Math.max(0, bar.left + barWidth / 2 - progressPopoverWidth / 2),
                      Math.max(0, timelineWidth - progressPopoverWidth),
                    );
                    return (
                      <div
                        key={task.id}
                        className="grid items-center py-2 border-t border-border/30"
                        style={{
                          gridTemplateColumns: `220px repeat(${ganttData.columns}, ${ganttData.columnWidth}px) 64px`,
                        }}
                      >
                        <div className="sticky left-0 self-stretch z-10 pr-4 bg-card border-r border-border/40">
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-xs font-medium">
                              {task.title}
                              {task.owner ? (
                                <span className="block text-[11px] text-muted-foreground">
                                  {task.owner}
                                </span>
                              ) : null}
                              {task.category ? (
                                <span className="block text-[10px] text-muted-foreground">
                                  {task.category}
                                </span>
                              ) : null}
                              <span className="block text-[10px] text-muted-foreground">
                                Időtartam: {durationDays} nap
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleStartEditTask(task)}
                                className="btn-secondary text-[10px] px-2 py-1"
                                title="Szerkesztés"
                                aria-label="Szerkesztés"
                              >
                                <Pencil size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteTask(task.id)}
                                className="btn-secondary text-[10px] text-destructive px-2 py-1"
                                title="Törlés"
                                aria-label="Törlés"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div
                          className={`group relative h-8 ${
                            isResizingTask || isMovingTask ? 'select-none' : ''
                          }`}
                          style={{
                            gridColumn: `2 / span ${ganttData.columns}`,
                            ...ganttData.timelineStyle,
                          }}
                        >
                          {ganttData.todayColumnStyle ? (
                            <>
                              <div
                                className="pointer-events-none absolute inset-y-0 bg-[#0F3D2C]/30 z-20"
                                style={ganttData.todayColumnStyle}
                                aria-hidden="true"
                              />
                            </>
                          ) : null}
                          <div
                            className="absolute inset-y-1 rounded-full ring-1 ring-black/10 shadow-sm"
                            style={{
                              left: bar.left,
                              width: barWidth,
                              backgroundColor: categoryColor,
                              opacity: 0.45,
                            }}
                          />
                          <div
                            className="absolute inset-y-1 rounded-full shadow-sm"
                            style={{
                              left: bar.left,
                              width: progressWidth,
                              backgroundColor: categoryColor,
                              opacity: 0.95,
                            }}
                          />
                          <button
                            type="button"
                            onPointerDown={(event) => handleMovePointerDown(event, task)}
                            onClick={() => setSelectedGanttTaskId(task.id)}
                            className={`absolute inset-y-1 rounded-full bg-transparent transition-shadow ${
                              isSelected
                                ? 'ring-2 ring-[#0F3D2C]/45'
                                : 'hover:ring-2 hover:ring-[#0F3D2C]/25'
                            }`}
                            style={{
                              left: bar.left,
                              width: barWidth,
                              cursor: isMovingTask ? 'grabbing' : 'grab',
                              boxShadow: outlineColor ? `0 0 0 2px ${outlineColor}` : undefined,
                            }}
                            title="Sáv húzása: áthelyezés, szélek húzása: időtartam"
                            aria-label={`Feladat áthelyezése: ${task.title}`}
                          />
                          <div
                            className={`absolute -top-20 z-30 rounded-lg border border-border/70 bg-card/95 px-2 py-2 shadow-lg backdrop-blur-sm transition-opacity ${
                              isResizingTask || isMovingTask
                                ? 'opacity-0 pointer-events-none'
                                : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto'
                            }`}
                            style={{
                              left: progressPopoverLeft,
                              width: progressPopoverWidth,
                            }}
                          >
                            <p className="text-[10px] text-muted-foreground mb-1">
                              Készültség: <span className="font-semibold">{task.progress}%</span>
                            </p>
                            <div className="grid grid-cols-3 gap-1">
                              {GANTT_PROGRESS_STEPS.map((value) => (
                                <button
                                  key={`${task.id}-progress-${value}`}
                                  type="button"
                                  disabled={updatingProgressTaskId === task.id}
                                  onClick={() => void handleSetGanttTaskProgress(task.id, value)}
                                  className={`rounded-md border px-1.5 py-0.5 text-[10px] transition-colors disabled:opacity-60 ${
                                    task.progress === value
                                      ? 'border-[#0F3D2C]/50 bg-[#0F3D2C]/10 text-[#0F3D2C]'
                                      : 'border-border/70 bg-card hover:bg-secondary'
                                  }`}
                                >
                                  {value}%
                                </button>
                              ))}
                            </div>
                          </div>
                          {isSelected ? (
                            <>
                              <button
                                type="button"
                                onPointerDown={(event) =>
                                  handleResizePointerDown(event, task, 'start')
                                }
                                className={`absolute top-1/2 h-6 w-3 -translate-y-1/2 rounded-full border border-[#0F3D2C]/50 bg-card shadow cursor-ew-resize ${
                                  resizeState?.taskId === task.id && resizeState?.edge === 'start'
                                    ? 'ring-2 ring-[#0F3D2C]/30'
                                    : 'hover:bg-secondary'
                                }`}
                                style={{ left: leftHandleLeft }}
                                title="Kezdés húzása"
                                aria-label={`Kezdés módosítása: ${task.title}`}
                              />
                              <button
                                type="button"
                                onPointerDown={(event) =>
                                  handleResizePointerDown(event, task, 'end')
                                }
                                className={`absolute top-1/2 h-6 w-3 -translate-y-1/2 rounded-full border border-[#0F3D2C]/50 bg-card shadow cursor-ew-resize ${
                                  resizeState?.taskId === task.id && resizeState?.edge === 'end'
                                    ? 'ring-2 ring-[#0F3D2C]/30'
                                    : 'hover:bg-secondary'
                                }`}
                                style={{ left: rightHandleLeft }}
                                title="Befejezés húzása"
                                aria-label={`Befejezés módosítása: ${task.title}`}
                              />
                            </>
                          ) : null}
                        </div>
                        <div className="sticky right-0 z-10 bg-transparent border-l border-border/40 flex justify-center">
                          <input
                            type="checkbox"
                            className="accent-emerald-600"
                            checked={task.completed}
                            onChange={(event) =>
                              handleToggleTaskDone(task.id, event.target.checked)
                            }
                            title="Kész"
                            aria-label="Kész"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}

function CompanyInfoEditor({
  companyInfo,
  loading,
  saving,
  isDirty,
  status,
  onChange,
  onSave,
  onDiscard,
}) {
  const fields = [
    { name: 'taxNumber', label: 'Adószám', placeholder: 'Pl. 12345678-2-41' },
    { name: 'bankAccountNumber', label: 'Bankszámlaszám', placeholder: 'Pl. 12345678-12345678-12345678' },
    { name: 'registryNumber', label: 'Törzsszám', placeholder: 'Add meg a törzsszámot' },
    { name: 'registrationNumber', label: 'Nyilvántartási szám', placeholder: 'Add meg a nyilvántartási számot' },
    { name: 'headquarters', label: 'Székhely', placeholder: 'Irányítószám, település, cím', fullWidth: true },
    { name: 'phone', label: 'Telefonszám', placeholder: '+36...', type: 'tel' },
    { name: 'email', label: 'Email cím', placeholder: 'pelda@ceg.hu', type: 'email' },
  ];

  return (
    <div className="mt-6 rounded-3xl border border-border/70 bg-card p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold">Céginfó</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            A vállalkozás legfontosabb hivatalos és kapcsolattartási adatai.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onDiscard}
            className="btn-secondary text-sm"
            disabled={!isDirty || saving}
          >
            Módosítások visszavonása
          </button>
          <button
            type="submit"
            form="company-info-form"
            className="btn-primary text-sm"
            disabled={!isDirty || saving || loading}
          >
            {saving ? 'Mentés...' : 'Mentés'}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Cégadatok betöltése...</p>
      ) : (
        <form id="company-info-form" onSubmit={onSave} className="grid gap-4 md:grid-cols-2">
          {fields.map((field) => (
            <div key={field.name} className={field.fullWidth ? 'md:col-span-2' : ''}>
              <label htmlFor={`company-${field.name}`} className="mb-1 block text-xs font-medium">
                {field.label}
              </label>
              <input
                id={`company-${field.name}`}
                type={field.type || 'text'}
                name={field.name}
                value={companyInfo[field.name]}
                onChange={onChange}
                className="input-zinga"
                placeholder={field.placeholder}
              />
            </div>
          ))}
          {status ? (
            <div
              className={`rounded-2xl border p-3 text-sm md:col-span-2 ${
                status.type === 'success'
                  ? 'border-[#B9D9B7] bg-[#E4F2E1] text-[#0F3D2C]'
                  : 'border-destructive/30 bg-destructive/10 text-destructive'
              }`}
            >
              {status.text}
            </div>
          ) : null}
        </form>
      )}
    </div>
  );
}

function LegalDocumentEditor({
  documentId,
  label,
  document,
  loading,
  saving,
  isDirty,
  status,
  onChange,
  onSave,
  onDiscard,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const usesRichText = documentId === 'terms' || documentId === 'privacy';
  const canExpand = usesRichText;

  useEffect(() => {
    if (!isExpanded) return undefined;

    const pageBody = window.document.body;
    const previousOverflow = pageBody.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }
    };

    pageBody.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      pageBody.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded]);

  return (
    <div
      className={
        isExpanded
          ? 'fixed inset-0 z-[100] flex h-[100dvh] flex-col overflow-hidden bg-background p-3 sm:p-4'
          : 'mt-6 rounded-3xl border border-border/70 bg-card p-6'
      }
    >
      <div
        className={`flex flex-wrap items-start justify-between gap-4 ${
          isExpanded ? 'mb-2 shrink-0 border-b border-border/70 pb-2' : 'mb-5'
        }`}
      >
        <div>
          <h2 className="font-display text-xl font-semibold">{label} szerkesztése</h2>
          {!isExpanded ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Mentés után a módosítás azonnal megjelenik a publikus oldalon.
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canExpand ? (
            <button
              type="button"
              onClick={() => setIsExpanded((current) => !current)}
              className="btn-secondary inline-flex items-center gap-2 text-sm"
              aria-expanded={isExpanded}
              title={isExpanded ? 'Vissza a normál nézethez (Esc)' : 'Nagyobb szerkesztő megnyitása'}
            >
              {isExpanded ? (
                <Minimize2 className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Maximize2 className="h-4 w-4" aria-hidden="true" />
              )}
              {isExpanded ? 'Normál nézet' : 'Nagyobb nézet'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onDiscard(documentId)}
            className="btn-secondary text-sm"
            disabled={!isDirty || saving}
          >
            Módosítások visszavonása
          </button>
          <button
            type="button"
            onClick={() => void onSave(documentId)}
            className="btn-primary text-sm"
            disabled={!isDirty || saving || loading}
          >
            {saving ? 'Mentés...' : 'Mentés és publikálás'}
          </button>
        </div>
      </div>
      {status ? (
        <div
          className={`${isExpanded ? 'mb-2 shrink-0 p-2' : 'mb-4 p-3'} rounded-2xl border text-sm ${
            status.type === 'success'
              ? 'border-[#B9D9B7] bg-[#E4F2E1] text-[#0F3D2C]'
              : 'border-destructive/30 bg-destructive/10 text-destructive'
          }`}
        >
          {status.text}
        </div>
      ) : null}
      {loading ? (
        <p className="text-sm text-muted-foreground">Dokumentum betöltése...</p>
      ) : (
        <div className={isExpanded ? 'flex min-h-0 flex-1 flex-col' : 'space-y-4'}>
          {!isExpanded ? (
            <div>
              <label
                htmlFor={`${documentId}-legal-title`}
                className="mb-1 block text-xs font-medium"
              >
                Oldal címe
              </label>
              <input
                id={`${documentId}-legal-title`}
                value={document.title}
                onChange={(event) => onChange(documentId, 'title', event.target.value)}
                className="input-zinga"
              />
            </div>
          ) : null}
          <div
            className={
              isExpanded
                ? 'flex min-h-0 flex-1 flex-col'
                : 'space-y-4'
            }
          >
            <div className={isExpanded ? 'flex min-h-0 flex-1 flex-col' : ''}>
              <label
                htmlFor={`${documentId}-legal-content`}
                className="mb-1 block text-xs font-medium"
              >
                Dokumentum szövege
              </label>
              {usesRichText ? (
                <RichLegalTextEditor
                  value={document.content}
                  onChange={(value) => onChange(documentId, 'content', value)}
                  expanded={isExpanded}
                  ariaLabel={`${label} formázott szövege`}
                />
              ) : (
                <textarea
                  id={`${documentId}-legal-content`}
                  value={document.content}
                  onChange={(event) => onChange(documentId, 'content', event.target.value)}
                  className={`w-full rounded-xl border border-border bg-card px-4 py-3 text-sm leading-relaxed outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                    isExpanded ? 'min-h-0 flex-1 resize-none' : 'min-h-[420px]'
                  }`}
                  placeholder="Írd ide a dokumentum teljes szövegét..."
                />
              )}
              {!isExpanded ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  {usesRichText
                    ? 'Wordből közvetlenül beilleszthető: a címsorok, kiemelések, listák, táblázatok és hivatkozások megmaradnak. A képek és Word-specifikus stílusok kimaradnak.'
                    : 'A # Címsor, ## Alcím és a további kettőskereszt-szintek címsorokat hoznak létre. Az üres sorok külön bekezdéseket jelentenek.'}
                </p>
              ) : null}
            </div>
            {!isExpanded ? (
              <div className="rounded-2xl border border-border/70 bg-background/60 p-5">
                <p className="mb-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Élő előnézet
                </p>
                <LegalDocumentContent content={document.content} />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
